<script setup lang="ts">
import Button from '@/components/Button.vue'
import Toggle from '@/components/Toggle.vue'
import CartUpsellUpgradeItem from '@/components/CartUpsellUpgradeItem.vue'
import type { Product } from '@/lib/types/Product'
import { reactive, ref, watch } from 'vue'

const props = defineProps<{
  title: string
  isBusy: boolean
  productSelections: {
    product: Product
    key: string
    selectedVariantId: string
    selectedSellingPlanId: string
    quantity: number
  }[]
}>()

const selections = reactive(
  props.productSelections.map((productSelection) => ({
    ...productSelection,
    selected: false,
  })),
)

const emit = defineEmits<{
  (e: 'skip'): void
  (
    e: 'upgrade',
    upgradeData: {
      key: string
      quantity: number
      sellingPlanId: string
    }[],
  ): void
}>()

const upgradeSelected = () => {
  const subscriptionSelections = selections.filter(
    (productSelection) => productSelection.selected,
  )
  if (subscriptionSelections.length === 0) {
    emit('skip')
  } else {
    emit(
      'upgrade',
      subscriptionSelections.map((productSelection) => ({
        key: productSelection.key,
        quantity: productSelection.quantity,
        sellingPlanId: productSelection.selectedSellingPlanId,
      })),
    )
  }
}

const allSubscribed = ref(false)
watch(
  selections,
  () => {
    allSubscribed.value = selections.every(
      (productSelection) => productSelection.selected,
    )
  },
  { deep: true, immediate: true },
)

const toggleAllSubscription = () => {
  if (allSubscribed.value) {
    selections.forEach((productSelection) => {
      productSelection.selected = false
    })
  } else {
    selections.forEach((productSelection) => {
      productSelection.selected = true
    })
  }
}
</script>

<template>
  <div class="CartUpsellAdditional">
    <div class="Stack Stack--tiny">
      <h5>{{ title }}</h5>
      <p class="CartUpsellAdditional__SubscribeAll">
        <Toggle :value="allSubscribed" @change="toggleAllSubscription" />
        <span>Subscribe to all products</span>
      </p>
      <div>
        <CartUpsellUpgradeItem
          v-for="productSelection in selections"
          :key="productSelection.key"
          :product-selection="productSelection"
          @toggle-subscribe="
            productSelection.selected = !productSelection.selected
          "
          @update-quantity="productSelection.quantity = $event"
          @update-selling-plan="productSelection.selectedSellingPlanId = $event"
        >
        </CartUpsellUpgradeItem>
      </div>
      <div class="Stack Stack--tiny">
        <Button
          :modifiers="['block']"
          @click="upgradeSelected"
          :loading="isBusy"
          :disabled="isBusy"
        >
          Continue to checkout
        </Button>
      </div>
    </div>
  </div>
</template>

<style>
.CartUpsellAdditional__SubscribeAll {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
}
</style>
